import React, { useState } from 'react';
import classNames from 'classnames/bind';
import { useNavigate } from 'react-router-dom';
import useMountEffect from '@restart/hooks/useMountEffect';
import { Form, Divider, Input, Row, Button, Col, message, Space, Select, Flex } from 'antd';

import styles from './new.module.scss';
import API from '../../helpers/API';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';
import { getAssignableUserRoleOptions } from '../../utils/userRoleOptions';

const cx = classNames.bind(styles);
const CUSTOMER_ROLES = ['CUSTOMER_MANAGER', 'CUSTOMER_USER'];

const Page = () => {
  const navigate = useNavigate();
  const requesterRole = localStorage.getItem('@h2tms/role');
  const [customers, setCustomers] = useState([]);
  const [user, setUser] = useState({
    id: '',
    password: '',
    rePassword: '',
    name: '',
    role: '',
    email: '',
    phone: '',
    memo: '',
    customerRid: '',
  });

  useMountEffect(() => {
    API.get('/customers').then(({ success, data }) => {
      if (success) {
        setCustomers(data.customers ?? []);
      }
    });
  });

  const handleChange = (e) => {
    setUser({
      ...user,
      [e.target.name]: e.target.value,
    });
  };

  const validation = () => {
    if (user.id.trim() === '') {
      message.error('아이디를 입력해 주세요.');
      return false;
    }
    if (user.password.trim() === '') {
      message.error('비밀번호를 입력해 주세요.');
      return false;
    }
    if (user.password !== user.rePassword) {
      message.error('비밀번호를 확인해 주세요.');
      return false;
    }
    if (user.name.trim() === '') {
      message.error('성명을 입력해 주세요.');
      return false;
    }
    if (user.role.trim() === '') {
      message.error('역할을 입력해 주세요.');
      return false;
    }
    if (CUSTOMER_ROLES.includes(user.role) && user.customerRid.trim() === '') {
      message.error('고객사를 선택해 주세요.');
      return false;
    }

    return true;
  };

  const doSave = () => {
    if (!validation()) {
      return;
    }

    const data = {
      id: user.id,
      password: user.password,
      name: user.name,
      role: user.role,
      email: user.email,
      phone: user.phone,
      memo: user.memo,
      customerRid: CUSTOMER_ROLES.includes(user.role) ? user.customerRid : null,
    };

    // API: 유저 신규 등록
    API.post('/users', data).then((response) => {
      const { success } = response;
      if (success) {
        navigate('/users');
      } else {
        message.error(getApiErrorMessage(response, '사용자 정보를 저장하지 못했습니다.'));
      }
    });
  };

  return (
    <div className={cx('container')}>
      <h1 className={cx('title')}>사용자 신규 등록</h1>
      <div className={cx('contentWrapper')}>
        <Form layout="vertical">
          <Divider orientation="left" orientationMargin={0} className={cx('divider')}>
            기본정보
          </Divider>
          <Flex vertical style={{ maxWidth: 640 }}>
            <Form.Item label="아이디" required>
              <Input name="id" placeholder="아이디" onChange={handleChange} style={{ width: 300 }} />
            </Form.Item>
            <Form.Item label="비밀번호" required>
              <Space align="center">
                <Input
                  name="password"
                  placeholder="비밀번호"
                  type="password"
                  onChange={handleChange}
                  style={{ width: 300 }}
                />
                <Input
                  name="rePassword"
                  placeholder="비밀번호 확인"
                  type="password"
                  onChange={handleChange}
                  style={{ width: 300 }}
                />
              </Space>
            </Form.Item>
            <Form.Item label="성명" required>
              <Input name="name" placeholder="성명" onChange={handleChange} style={{ width: 300 }} />
            </Form.Item>
            <Form.Item label="역할" required>
              <Select
                placeholder="선택"
                name="role"
                onChange={(value) =>
                  setUser({
                    ...user,
                    role: value,
                    customerRid: CUSTOMER_ROLES.includes(value) ? user.customerRid : '',
                  })
                }
                options={getAssignableUserRoleOptions(requesterRole)}
                style={{ width: 300 }}
              />
            </Form.Item>
            {CUSTOMER_ROLES.includes(user.role) && (
              <Form.Item label="고객사" required>
                <Select
                  placeholder="고객사 선택"
                  value={user.customerRid || undefined}
                  onChange={(value) => setUser({ ...user, customerRid: value })}
                  options={customers.map((customer) => ({
                    value: customer.rid,
                    label: customer.status === 'INACTIVE' ? `${customer.name} (비활성)` : customer.name,
                  }))}
                  style={{ width: 300 }}
                />
              </Form.Item>
            )}
            <Form.Item label="이메일">
              <Input name="email" placeholder="이메일" onChange={handleChange} style={{ width: 300 }} />
            </Form.Item>
            <Form.Item label="전화번호">
              <Input name="phone" placeholder="전화번호" onChange={handleChange} style={{ width: 300 }} />
            </Form.Item>
          </Flex>
          <Divider orientation="left" orientationMargin={0} className={cx('divider')}>
            부가정보
          </Divider>
          <Row style={{ maxWidth: 640 }}>
            <Col span={24}>
              <Form.Item label="메모">
                <Input.TextArea
                  name="memo"
                  placeholder="메모"
                  onChange={handleChange}
                  rows={5}
                  className={cx('textArea')}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <Row align="middle" justify={'space-between'}>
        <Button onClick={() => navigate(-1)}>뒤로</Button>
        <Button type="primary" onClick={doSave}>
          저장
        </Button>
      </Row>
    </div>
  );
};

export default Page;
